import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import {
  Users,
  Clock,
  ChartLine,
  TrendUp,
  DownloadSimple,
  ArrowRight,
  Trophy,
  UserMinus,
  MagnifyingGlass,
  BookOpen,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError, API } from "@/lib/api";
import { formatDuration, formatRelative, shortDate, tickDate } from "@/lib/analyticsFormat";
import AnalyticsHeatmap from "@/pages/admin/AnalyticsHeatmap";

const RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("total_watched_seconds");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [ov, emp, crs] = await Promise.all([
          api.get(`/analytics/overview?days=${days}`),
          api.get(`/analytics/employees`),
          api.get(`/courses`),
        ]);
        if (!alive) return;
        setOverview(ov.data);
        setEmployees(emp.data);
        setCourses(crs.data || []);
      } catch (e) {
        setError(formatApiError(e, "Could not load analytics."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [days]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = !q
      ? employees
      : employees.filter(
          (r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.email || "").toLowerCase().includes(q)
        );
    rows = [...rows].sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === "string" || typeof bv === "string") {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [employees, search, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const sortChevron = (key) =>
    sortBy === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const summaryCards = overview
    ? [
        {
          label: "Total watch time",
          value: formatDuration(overview.total_watched_seconds),
          icon: Clock,
          testid: "analytics-card-total-watch",
        },
        {
          label: "Employees",
          value: overview.total_employees,
          icon: Users,
          testid: "analytics-card-employees",
        },
        {
          label: `Inactive (30d)`,
          value: overview.inactive_30d,
          icon: UserMinus,
          testid: "analytics-card-inactive",
          danger: true,
        },
        {
          label: `Last ${days}d activity peak`,
          value: formatDuration(Math.max(...(overview.daily || []).map((d) => d.seconds), 0)),
          icon: TrendUp,
          testid: "analytics-card-peak",
        },
      ]
    : [];

  const dailyChartData = (overview?.daily || []).map((d) => ({
    date: d.date,
    minutes: +(d.seconds / 60).toFixed(1),
    activeUsers: d.active_users,
  }));

  const leaderboardChart = (overview?.leaderboard || []).slice(0, 8).map((r) => ({
    name: r.name?.split(" ")[0] || r.email?.split("@")[0],
    minutes: +(r.seconds / 60).toFixed(1),
  }));

  const handleExport = () => {
    // Open in a new tab — backend forces download via Content-Disposition.
    window.open(`${API}/analytics/export.csv`, "_blank");
  };

  return (
    <AppLayout
      title="Training Analytics"
      eyebrow="Engagement & metrics"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#FAF7F2] border border-[#E5E2DC] rounded-md p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                data-testid={`analytics-range-${r.value}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                  days === r.value
                    ? "bg-white text-[#1C1C19] shadow-sm"
                    : "text-[#5C5C58] hover:text-[#1C1C19]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            data-testid="analytics-export-csv"
            className="px-4 py-2 bg-[#1C1C19] hover:bg-black text-white text-sm font-medium inline-flex items-center gap-2 rounded-md"
          >
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
        </div>
      }
    >
      {error && (
        <div className="text-sm text-[#993333] mb-4" data-testid="analytics-error">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {summaryCards.map((c) => (
          <div
            key={c.label}
            data-testid={c.testid}
            className={`bg-white border ${
              c.danger && c.value > 0 ? "border-[#E25822]/40" : "border-[#E5E2DC]"
            } p-5 rounded-md`}
          >
            <div className="flex items-center justify-between mb-3">
              <c.icon
                size={20}
                weight="duotone"
                className={c.danger && c.value > 0 ? "text-[#E25822]" : "text-[#5C5C58]"}
              />
            </div>
            <div className="font-heading text-3xl font-semibold text-[#1C1C19] mb-1">
              {loading ? "—" : c.value}
            </div>
            <div className="text-xs uppercase tracking-wider text-[#5C5C58] font-medium">
              {c.label}
            </div>
          </div>
        ))}
      </div>

      {/* Daily watch chart + leaderboard side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 bg-white border border-[#E5E2DC] p-5 rounded-md" data-testid="analytics-daily-chart">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="font-heading text-lg font-semibold text-[#1C1C19]">Daily training activity</div>
              <div className="text-xs text-[#5C5C58]">Total minutes watched across the org · last {days} days</div>
            </div>
            <ChartLine size={20} weight="duotone" className="text-[#E25822]" />
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={dailyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFECE5" />
                <XAxis dataKey="date" tickFormatter={tickDate} stroke="#5C5C58" fontSize={11} />
                <YAxis stroke="#5C5C58" fontSize={11} />
                <Tooltip
                  formatter={(v, k) => [k === "minutes" ? `${v} min` : `${v} users`, k === "minutes" ? "Watched" : "Active"]}
                  labelFormatter={(d) => shortDate(d)}
                  contentStyle={{ background: "#1C1C19", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                />
                <Line type="monotone" dataKey="minutes" stroke="#E25822" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="activeUsers" stroke="#5C5C58" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E5E2DC] p-5 rounded-md" data-testid="analytics-leaderboard">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading text-lg font-semibold text-[#1C1C19]">Top learners</div>
              <div className="text-xs text-[#5C5C58]">Last {days} days</div>
            </div>
            <Trophy size={20} weight="duotone" className="text-[#E25822]" />
          </div>
          {leaderboardChart.length === 0 ? (
            <div className="text-sm text-[#8A8A85] py-8 text-center">No watch activity yet in this range.</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={leaderboardChart} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFECE5" />
                  <XAxis type="number" stroke="#5C5C58" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#5C5C58" fontSize={11} width={70} />
                  <Tooltip
                    formatter={(v) => [`${v} min`, "Watched"]}
                    contentStyle={{ background: "#1C1C19", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  />
                  <Bar dataKey="minutes" fill="#E25822" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* By course — quick drill-in cards */}
      <div className="mb-10" data-testid="analytics-courses-section">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">By course</div>
            <div className="text-xs text-[#5C5C58]">Click any course to see per-lesson engagement</div>
          </div>
        </div>
        {courses.length === 0 ? (
          <div className="bg-white border border-[#E5E2DC] rounded-md p-8 text-sm text-[#8A8A85] text-center">
            No courses yet — create one in <Link to="/admin/courses" className="text-[#E25822] font-semibold">Manage Courses</Link>.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {courses.map((c) => {
              const totalLessons = (c.modules || []).reduce(
                (s, m) => s + (m.lessons?.length || 0),
                0
              );
              return (
                <Link
                  key={c.id}
                  to={`/admin/analytics/course/${c.id}`}
                  data-testid={`analytics-course-card-${c.id}`}
                  className="group bg-white border border-[#E5E2DC] hover:border-[#E25822] rounded-md p-4 transition-colors flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded bg-[#E25822]/10 text-[#E25822] flex items-center justify-center shrink-0 group-hover:bg-[#E25822] group-hover:text-white transition-colors">
                    <BookOpen size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#1C1C19] truncate group-hover:text-[#E25822] transition-colors">
                      {c.title}
                    </div>
                    <div className="text-xs text-[#5C5C58] mt-0.5 flex items-center gap-2">
                      <span>{(c.modules || []).length} modules</span>
                      <span className="text-[#D8D8D2]">·</span>
                      <span>{totalLessons} lessons</span>
                      {!c.published && (
                        <>
                          <span className="text-[#D8D8D2]">·</span>
                          <span className="text-[#A8651A] font-semibold">Draft</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily breakdown + heatmap */}
      <div className="mb-10" data-testid="analytics-daily-breakdown-wrap">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">Daily activity per employee</div>
            <div className="text-xs text-[#5C5C58]">Pick a day for a table view, or use the heatmap to spot patterns</div>
          </div>
        </div>
        <AnalyticsHeatmap days={days} />
      </div>

      {/* Employees table */}
      <div className="bg-white border border-[#E5E2DC] rounded-md overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between border-b border-[#E5E2DC]">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">All employees</div>
            <div className="text-xs text-[#5C5C58]">
              {filteredEmployees.length} {filteredEmployees.length === 1 ? "person" : "people"} · click a row for daily drill-down
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85]" />
            <input
              type="text"
              data-testid="analytics-employee-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-8 pr-3 py-2 text-sm border border-[#E5E2DC] rounded-md w-64 focus:outline-none focus:border-[#E25822]"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-employees-table">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5C5C58]">
              <tr>
                <Th onClick={() => toggleSort("name")} label={`Employee${sortChevron("name")}`} />
                <Th onClick={() => toggleSort("total_watched_seconds")} label={`Watch time${sortChevron("total_watched_seconds")}`} align="right" />
                <Th onClick={() => toggleSort("lessons_completed")} label={`Completed${sortChevron("lessons_completed")}`} align="right" />
                <Th onClick={() => toggleSort("courses_enrolled")} label={`Enrolled${sortChevron("courses_enrolled")}`} align="right" />
                <Th onClick={() => toggleSort("active_days_30d")} label={`Active days (30d)${sortChevron("active_days_30d")}`} align="right" />
                <Th onClick={() => toggleSort("last_login_at")} label={`Last login${sortChevron("last_login_at")}`} />
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-[#8A8A85]">No employees match this filter.</td>
                </tr>
              )}
              {filteredEmployees.map((r) => (
                <tr
                  key={r.user_id}
                  className="border-t border-[#EFECE5] hover:bg-[#FAF7F2] transition-colors"
                  data-testid={`analytics-row-${r.user_id}`}
                >
                  <td className="px-5 py-3">
                    <div className="font-semibold text-[#1C1C19]">{r.name || "—"}</div>
                    <div className="text-xs text-[#8A8A85]">{r.email}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-[#1C1C19]">
                    {formatDuration(r.total_watched_seconds)}
                  </td>
                  <td className="px-5 py-3 text-right text-[#5C5C58]">{r.lessons_completed}</td>
                  <td className="px-5 py-3 text-right text-[#5C5C58]">{r.courses_enrolled}</td>
                  <td className="px-5 py-3 text-right text-[#5C5C58]">{r.active_days_30d}</td>
                  <td className="px-5 py-3 text-[#5C5C58]" title={r.last_login_at || ""}>
                    {formatRelative(r.last_login_at)}
                  </td>
                  <td className="px-3">
                    <Link
                      to={`/admin/analytics/employee/${r.user_id}`}
                      data-testid={`analytics-row-link-${r.user_id}`}
                      className="text-[#E25822] hover:text-[#C44E1A] inline-flex"
                    >
                      <ArrowRight size={16} weight="bold" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}

function Th({ label, onClick, align = "left" }) {
  return (
    <th
      onClick={onClick}
      className={`px-5 py-3 font-semibold cursor-pointer select-none hover:text-[#1C1C19] text-${align}`}
    >
      {label}
    </th>
  );
}
